import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faLock } from '@fortawesome/free-solid-svg-icons';
import { resetPassword } from '../services/api';
import theme from '../styles/theme';

const ResetPasswordContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  width: 100%;
  background: linear-gradient(145deg, ${theme.colors.sidebarFrom} 0%, ${theme.colors.sidebarTo} 45%, #243044 100%);
  position: fixed;
  inset: 0;
  padding: 72px 20px 24px;
  box-sizing: border-box;
  overflow-y: auto;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse 60% 50% at 20% 20%, rgba(255, 107, 107, 0.2) 0%, transparent 50%),
      radial-gradient(ellipse 50% 40% at 80% 80%, rgba(255, 142, 83, 0.14) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const HomeLink = styled(Link)`
  position: fixed;
  top: 20px;
  left: 20px;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  padding: 10px 18px;
  border-radius: ${theme.radius.pill};
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(12px);
  transition: all ${theme.transitions.base};
  font-size: ${theme.typography.body};
  z-index: 2;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
    transform: translateY(-2px);
  }
`;

const ResetPasswordCard = styled.div`
  background: ${theme.colors.card};
  padding: 36px 32px;
  border-radius: ${theme.radius.xl};
  box-shadow: ${theme.shadows.xl}, 0 0 0 1px rgba(255, 255, 255, 0.08);
  width: 100%;
  max-width: 420px;
  margin: auto;
  position: relative;
  z-index: 1;
  animation: slideUp 0.45s cubic-bezier(0.34, 1.2, 0.64, 1);

  @media (min-width: 1024px) {
    max-width: 460px;
    padding: 44px 40px;
  }
`;

const IconBadge = styled.div`
  width: 56px;
  height: 56px;
  border-radius: ${theme.radius.lg};
  background: rgba(255, 107, 107, 0.1);
  border: 1px solid rgba(255, 107, 107, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: ${theme.colors.primary};
  font-size: 22px;
`;

const Title = styled.h1`
  font-size: ${theme.typography.h1};
  font-weight: 800;
  margin: 0 0 8px;
  text-align: center;
  background: ${theme.colors.accentGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.2;
  letter-spacing: -0.04em;
`;

const Subtitle = styled.p`
  text-align: center;
  color: ${theme.colors.textMuted};
  margin: 0 0 28px;
  font-size: ${theme.typography.body};
  line-height: 1.6;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 100%;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;

const Label = styled.label`
  font-size: ${theme.typography.small};
  font-weight: 600;
  color: ${theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RequiredAsterisk = styled.span`
  color: ${theme.colors.error};
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const InputIcon = styled.span`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.textMuted};
  font-size: 16px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px 8px 36px;
  border: 1.5px solid ${theme.colors.border};
  border-radius: ${theme.radius.md};
  font-size: ${theme.typography.body};
  transition: all ${theme.transitions.base};
  background: ${theme.colors.card};
  box-sizing: border-box;
  height: 44px;
  color: ${theme.colors.text};

  &:focus {
    border-color: ${theme.colors.primary};
    outline: none;
    box-shadow: 0 0 0 4px rgba(255, 107, 107, 0.12);
  }

  &.error {
    border-color: ${theme.colors.error};
    box-shadow: 0 0 0 4px ${theme.colors.errorBg};
  }
`;

const ErrorText = styled.span`
  color: ${theme.colors.error};
  font-size: ${theme.typography.small};
  margin-top: 4px;
`;

const SubmitButton = styled.button`
  background: ${theme.colors.accentGradient};
  color: white;
  padding: 10px;
  border-radius: ${theme.radius.md};
  font-weight: 700;
  font-size: ${theme.typography.body};
  border: none;
  cursor: pointer;
  transition: all ${theme.transitions.base};
  width: 100%;
  margin-top: 10px;
  height: 48px;
  box-shadow: ${theme.shadows.glowSoft};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.glow};
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    transform: none;
  }
`;

const BackToLogin = styled(Link)`
  color: ${theme.colors.primary};
  text-align: center;
  display: block;
  margin-top: 20px;
  text-decoration: none;
  font-weight: 600;
  font-size: ${theme.typography.body};
  padding: 8px;
  border-radius: ${theme.radius.sm};
  transition: all ${theme.transitions.fast};

  &:hover {
    color: ${theme.colors.secondary};
    background: rgba(255, 107, 107, 0.08);
  }
`;

interface FormData {
  password: string;
  confirmPassword: string;
}

interface FormErrors {
  password?: string;
  confirmPassword?: string;
}

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState<FormData>({
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);

  const token = new URLSearchParams(location.search).get('token');

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.password) {
      newErrors.password = 'Oops! You forgot to enter your password.';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Too short — your password must be 6 characters minimum.';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/.test(formData.password)) {
      newErrors.password =
        'Your password needs a capital letter, lowercase letter, number, and special symbol.';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      toast.error('Invalid or expired reset link');
      return;
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await resetPassword({
        token,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
      });

      toast.success(response.message || 'Password has been reset successfully');
      navigate('/login');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reset password';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <ResetPasswordContainer>
        <HomeLink to="/login">
          <FontAwesomeIcon icon={faHome} /> Back to Login
        </HomeLink>
        <ResetPasswordCard>
          <IconBadge>
            <FontAwesomeIcon icon={faLock} />
          </IconBadge>
          <Title>Invalid Link</Title>
          <Subtitle>This password reset link is invalid or has expired.</Subtitle>
          <BackToLogin to="/login">Back to Login</BackToLogin>
        </ResetPasswordCard>
      </ResetPasswordContainer>
    );
  }

  return (
    <ResetPasswordContainer>
      <HomeLink to="/login">
        <FontAwesomeIcon icon={faHome} /> Back to Login
      </HomeLink>
      <ResetPasswordCard>
        <IconBadge>
          <FontAwesomeIcon icon={faLock} />
        </IconBadge>
        <Title>Reset Password</Title>
        <Subtitle>Choose a strong new password for your account</Subtitle>
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>
              New Password<RequiredAsterisk>*</RequiredAsterisk>
            </Label>
            <InputWrapper>
              <InputIcon>
                <FontAwesomeIcon icon={faLock} />
              </InputIcon>
              <Input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={errors.password ? 'error' : ''}
                placeholder="Enter new password"
              />
            </InputWrapper>
            {errors.password && <ErrorText>{errors.password}</ErrorText>}
          </FormGroup>

          <FormGroup>
            <Label>
              Confirm Password<RequiredAsterisk>*</RequiredAsterisk>
            </Label>
            <InputWrapper>
              <InputIcon>
                <FontAwesomeIcon icon={faLock} />
              </InputIcon>
              <Input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={errors.confirmPassword ? 'error' : ''}
                placeholder="Confirm new password"
              />
            </InputWrapper>
            {errors.confirmPassword && <ErrorText>{errors.confirmPassword}</ErrorText>}
          </FormGroup>

          <SubmitButton type="submit" disabled={loading}>
            {loading ? 'Resetting Password...' : 'Reset Password'}
          </SubmitButton>
        </Form>

        <BackToLogin to="/login">Back to Login</BackToLogin>
      </ResetPasswordCard>
    </ResetPasswordContainer>
  );
};

export default ResetPassword;
